// Shared validation helpers for admin forms.

// Standard Indian GSTIN format:
//   ^[0-9]{2}[A-Z]{5}[0-9]{4}[A-Z]{1}[1-9A-Z]{1}Z[0-9A-Z]{1}$
//   15 characters: 2-digit state code, 10-char PAN, entity code, Z, check digit.
const GST_PATTERN = /^[0-9]{2}[A-Z]{5}[0-9]{4}[A-Z]{1}[1-9A-Z]{1}Z[0-9A-Z]{1}$/;

// Special accepted value for Unregistered Persons (URP). Standard GSTIN pattern
// validation is bypassed when the entered value is exactly this code.
export const GST_URP_CODE = "URP";

// GSTIN segment definitions, in order:
//   [0-9]{2} | [A-Z]{5} | [0-9]{4} | [A-Z]{1} | [1-9A-Z]{1} | Z | [0-9A-Z]{1}
export const GST_SEGMENTS = [
  { pattern: "[0-9]{2}", length: 2, label: "State code" },
  { pattern: "[A-Z]{5}", length: 5, label: "PAN (letters)" },
  { pattern: "[0-9]{4}", length: 4, label: "PAN serial" },
  { pattern: "[A-Z]{1}", length: 1, label: "Entity code" },
  { pattern: "[1-9A-Z]{1}", length: 1, label: "Entity no." },
  { pattern: "Z", length: 1, label: "Z" },
  { pattern: "[0-9A-Z]{1}", length: 1, label: "Check digit" },
];

// Returns the index of the segment the user is currently typing in (0-6),
// based on the length of the value entered so far. Returns -1 for empty values.
export const getGSTSegmentProgress = (gst) => {
  const normalized = String(gst || "").trim().toUpperCase();
  if (!normalized) return -1;

  let charactersUsed = 0;
  for (let i = 0; i < GST_SEGMENTS.length; i++) {
    charactersUsed += GST_SEGMENTS[i].length;
    if (normalized.length <= charactersUsed) return i;
  }
  return GST_SEGMENTS.length; // Complete (15 or more chars).
};

// Returns per-segment state for the GST hint. Each segment gets:
//   isComplete - fully filled AND its characters match the segment pattern
//   isError    - fully filled but its characters do NOT match the pattern
//   isActive   - the segment currently being typed (not yet full)
export const getGSTSegmentStates = (gst) => {
  const normalized = String(gst || "").trim().toUpperCase();
  const states = [];

  let cumulative = 0;
  for (let i = 0; i < GST_SEGMENTS.length; i++) {
    const segment = GST_SEGMENTS[i];
    const start = cumulative;
    cumulative += segment.length;
    const end = cumulative;

    const entered = normalized.slice(start, end);
    const isFull = normalized.length >= end;
    const matches = isFull && new RegExp(`^${segment.pattern}$`).test(entered);

    states.push({
      isComplete: isFull && matches,
      isError: isFull && !matches,
      isActive: !isFull && normalized.length >= start,
    });
  }

  return states;
};

export const isValidGSTNumber = (gst) => {
  const normalized = String(gst || "").trim().toUpperCase();
  if (!normalized) return true; // GST is optional; empty is allowed.
  if (normalized === GST_URP_CODE) return true; // Unregistered Person (URP) is allowed.
  return GST_PATTERN.test(normalized);
};

export const getGSTValidationMessage = (gst) => {
  const normalized = String(gst || "").trim().toUpperCase();
  if (!normalized) return "";
  if (GST_PATTERN.test(normalized) || normalized === GST_URP_CODE) return "";
  return "Invalid GST number. Expected format: 2 digits + 10-char PAN + entity code + Z + check digit (e.g. 24ABCDE1234F1Z5).";
};

// Validates a 10-digit Indian mobile number. Accepts either a plain 10-digit
// number or one with an optional "+91" / "91" country-code prefix, tolerating
// spaces and dashes. A 10-digit number that itself starts with "91"
// (e.g. 9198765432) is treated as a valid local number rather than being
// mistaken for a country-code prefix (which would otherwise over-strip it).
export const isValidMobileNumber = (mobile) => {
  const digits = String(mobile || "").trim().replace(/\D/g, "");
  if (/^[6-9]\d{9}$/.test(digits)) return true;   // plain 10-digit number
  if (/^91[6-9]\d{9}$/.test(digits)) return true; // 91 / +91 + 10 digits
  return false;
};

// Returns an error message for an invalid/empty mobile number, or "" if valid.
export const getMobileValidationMessage = (mobile) => {
  const normalized = String(mobile || "").trim();
  if (!normalized) return "Mobile number is required.";
  if (!isValidMobileNumber(normalized)) return "Invalid mobile number. Enter a valid 10-digit Indian mobile number.";
  return "";
};